// solution from https://github.com/vercel/next.js/discussions/32231#discussioncomment-6351802
import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

const errorMessage =
  'Aborting navigation due to unsaved changes. Please ignore this error.';

const throwFakeErrorToFoolNextRouter = () => {
  // Throwing an actual error class trips the Next.JS 500 Page, this string literal does not.
  throw errorMessage;
};

const rejectionHandler = (event: PromiseRejectionEvent) => {
  if (event.reason === errorMessage) event.preventDefault();
};

interface Props {
  onNavigate: () => void;
  shouldStopNavigation: boolean;
}

const useNavigationObserver = ({ onNavigate, shouldStopNavigation }: Props) => {
  const nextPath = useRef('');
  const navigationConfirmed = useRef(false);

  const router = useRouter();

  const currentPath = router.asPath;

  const killRouterEvent = useCallback(() => {
    router.events.emit('routeChangeError', '', '', { shallow: false });
    throwFakeErrorToFoolNextRouter();
  }, [router]);

  useEffect(() => {
    navigationConfirmed.current = false;

    const onRouteChange = (url: string) => {
      if (currentPath !== url) {
        // if the user clicked on the browser back button then the url displayed in the browser gets incorrectly updated
        // This is needed to restore the correct url.
        // note: history.replaceState does not trigger a page reload
        // https://github.com/vercel/next.js/discussions/32231#discussioncomment-7263046
        window.history.replaceState(
          window.history.state,
          '',
          router.basePath + currentPath
        );
      }

      if (
        shouldStopNavigation &&
        url !== currentPath &&
        !navigationConfirmed.current
      ) {
        // removing the basePath from the url as it will be added by the router
        nextPath.current = url.replace(router.basePath, '');
        onNavigate();
        killRouterEvent();
      }
    };

    router.events.on('routeChangeStart', onRouteChange);
    window.addEventListener('unhandledrejection', rejectionHandler);

    return () => {
      router.events.off('routeChangeStart', onRouteChange);
      window.removeEventListener('unhandledrejection', rejectionHandler);
    };
  }, [
    currentPath,
    killRouterEvent,
    onNavigate,
    router.basePath,
    router.events,
    shouldStopNavigation
  ]);

  const confirmNavigation = () => {
    navigationConfirmed.current = true;
    router.push(nextPath.current);
  };

  return confirmNavigation;
};

export default useNavigationObserver;
